import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, ArrowRight, Shield, KeyRound, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguageStore, useAuthStore, useAppearanceStore } from '../store';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { getRecaptchaToken } from '../lib/recaptcha';
import { trackEvent } from '../lib/analytics';

const pwdHints = {
  en: { reqs: 'Password requirements:', len: 'Min 8 characters', up: '1 uppercase letter', low: '1 lowercase letter', num: '1 digit', spec: '1 special character (!@#...)' },
  ru: { reqs: 'Требования к паролю:', len: 'Минимум 8 символов', up: '1 заглавная буква', low: '1 строчная буква', num: '1 цифра', spec: '1 спецсимвол (!@#...)' },
  nl: { reqs: 'Wachtwoordvereisten:', len: 'Minimaal 8 tekens', up: '1 hoofdletter', low: '1 kleine letter', num: '1 cijfer', spec: '1 speciaal teken (!@#...)' },
  ro: { reqs: 'Cerințe parolă:', len: 'Minim 8 caractere', up: '1 literă mare', low: '1 literă mică', num: '1 cifră', spec: '1 caracter special (!@#...)' }
};

export const LoginPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const { login, isLoading } = useAuthStore();
  const navigate = useNavigate();

  const siteImages = useAppearanceStore((s) => s.config?.images || {});
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isImgReady, setIsImgReady] = useState(false);

  // 🛡️ Состояния Двухфакторной Аутентификации (2FA)
  const [requires2FA, setRequires2FA] = useState(false);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const otpInputsRef = useRef([]);

  // Таймер повторной отправки кода
  useEffect(() => {
    let interval = null;
    if (requires2FA && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
      if (interval) clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [requires2FA, resendTimer]);

  // Фокус на первую ячейку OTP
  useEffect(() => {
    if (requires2FA && otpInputsRef.current[0]) {
      otpInputsRef.current[0].focus();
    }
  }, [requires2FA]);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otpCode];
    newOtp[index] = value.slice(-1);
    setOtpCode(newOtp);

    if (value && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      setOtpCode(pastedData.split(''));
      otpInputsRef.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const fullOtp = otpCode.join('');
      const response = await login(email, password, fullOtp);

      if (response && response.status === '2fa_required') {
        setRequires2FA(true);
        setResendTimer(60);
        setCanResend(false);
        toast.info(response.message || 'Security code sent to your Telegram & Email');
        return;
      }

      toast.success(t.auth.welcomeBack);
      navigate(`/${language}`);
    } catch (error) {
      toast.error(error.message || t.auth.loginFailed);
    }
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    setCanResend(false);
    setResendTimer(60);
    setOtpCode(['', '', '', '', '', '']);
    try {
      const response = await login(email, password, '');
      if (response && response.status === '2fa_required') {
        toast.success('A new 6-digit code has been dispatched');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to resend verification code');
      setCanResend(true);
    }
  };

  const loginImgUrl = useMemo(() => {
    const cmsUrl = siteImages['auth_login']?.url || siteImages['auth_login'] || siteImages['about_hero']?.url || siteImages['about_hero'];
    let img = cmsUrl ? cmsUrl : "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80";
    if (img && typeof img === 'string' && img.includes('/upload/')) {
      img = img.replace('/upload/', '/upload/w_1200,q_auto,f_auto/');
    }
    return img;
  }, [siteImages]);

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Image Side */}
      <div className="hidden lg:block lg:w-1/2 relative bg-[#1A1A1A]">
        <img
          src={loginImgUrl}
          alt="Interior"
          decoding="async"
          onLoad={() => setIsImgReady(true)}
          className={`w-full h-full object-cover transition-opacity duration-300 ${isImgReady ? 'opacity-100' : 'opacity-0'}`}
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Form Side */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Link to={`/${language}`} className="inline-block mb-12">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-wide text-[#0A0A0A]">
              FEDYK <span className="font-light tracking-normal">Furniture</span>
            </span>
          </Link>

          {!requires2FA ? (
            <>
              <h1 className="text-h1 mb-2">{t.auth.login}</h1>
              <p className="text-muted-foreground mb-8">
                {t.auth.loginSubtitle}
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="email">{t.auth.email}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 mt-2"
                    required
                    data-testid="login-email"
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password">{t.auth.password}</Label>
                    <Link
                      to={`/${language}/forgot-password`}
                      className="text-sm text-muted-foreground hover:text-foreground"
                    >
                      {t.auth.forgotPassword}
                    </Link>
                  </div>
                  <div className="relative mt-2">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 pr-12"
                      required
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={isLoading}
                  data-testid="login-submit"
                >
                  {isLoading ? t.auth.signingIn : t.auth.login}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>

              <div className="mt-8 text-center">
                <p className="text-muted-foreground">
                  {t.auth.noAccount}{' '}
                  <Link
                    to={`/${language}/register`}
                    className="text-foreground hover:underline"
                    data-testid="register-link"
                  >
                    {t.auth.register}
                  </Link>
                </p>
              </div>

              <div className="mt-8 pt-8 border-t border-border text-center">
                <p className="text-sm text-muted-foreground mb-4">{t.auth.areYouPro}</p>
                <Link to={`/${language}/register?account=trade`}>
                  <Button variant="outline" className="btn-secondary" data-testid="b2b-register-link">
                    {t.auth.b2bRegister}
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            /* 🛡️ ШАГ 2: ВВОД 2FA OTP КОДА */
            <motion.form
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-6 h-6 text-[#0A0A0A]" />
                </div>
                <h2 className="text-xl font-light text-[#0A0A0A]">Two-Factor Authentication</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the 6-digit verification code sent to Telegram & Email
                </p>
              </div>

              <div className="flex justify-between gap-2" onPaste={handlePaste}>
                {otpCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputsRef.current[index] = el)}
                    type="text"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-14 text-center text-xl font-mono text-[#0A0A0A] border border-input bg-background focus:border-foreground outline-none rounded-md transition-all"
                  />
                ))}
              </div>

              <Button
                type="submit"
                className="btn-primary w-full h-12"
                disabled={isLoading || otpCode.join('').length < 6}
              >
                {isLoading ? 'Verifying Code...' : 'Verify & Enter Console'}
                <KeyRound className="ml-2 w-4 h-4" />
              </Button>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <button
                  type="button"
                  onClick={() => {
                    setRequires2FA(false);
                    setOtpCode(['', '', '', '', '', '']);
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Back to login</span>
                </button>

                {canResend ? (
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="text-xs text-foreground hover:underline flex items-center gap-1 font-medium"
                  >
                    <RefreshCw className="w-3 h-3" />
                    <span>Resend Code</span>
                  </button>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    Resend in <strong className="text-foreground">{resendTimer}s</strong>
                  </span>
                )}
              </div>
            </motion.form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const { register, isLoading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const hints = pwdHints[language] || pwdHints.en;

  const siteImages = useAppearanceStore((s) => s.config?.images || {});
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    is_b2b: false,
    company_name: '',
    vat_number: '',
    portfolio_url: '',
    role: 'customer',
    preferred_language: language,
    country: '',
    city: '',
    street_address: '',
    postal_code: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isImgReady, setIsImgReady] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('account') === 'trade') {
      setFormData((prev) => ({
        ...prev,
        is_b2b: true,
        role: 'architect'
      }));
    }
  }, [location]);

  const pwd = formData.password;
  const reqLen = pwd.length >= 8;
  const reqUpper = /[A-Z]/.test(pwd);
  const reqLower = /[a-z]/.test(pwd);
  const reqDigit = /[0-9]/.test(pwd);
  const reqSpecial = /[!@#$%^&*(),.?":{}|<>_\-]/.test(pwd);
  const isPasswordValid = reqLen && reqUpper && reqLower && reqDigit && reqSpecial;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'is_b2b') {
      setFormData((prev) => ({
        ...prev,
        is_b2b: checked,
        role: checked ? 'architect' : 'customer',
      }));
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      toast.error(hints.reqs);
      return;
    }

    try {
      const action = formData.is_b2b ? 'b2b_registration_submitted' : 'lead_form_completed';
      const recaptcha_token = await getRecaptchaToken(action);
      await register({ ...formData, recaptcha_token });

      if (formData.is_b2b) {
        trackEvent('b2b_registration_submitted', { role: formData.role });
        toast.success(t.auth.registrationSuccess);
        navigate(`/${language}/b2b`);
      } else {
        trackEvent('lead_form_completed', { source: 'registration' });
        toast.success(t.auth.registrationSuccess);
        navigate(`/${language}/login`);
      }
    } catch (error) {
      toast.error(error.message || t.auth.registrationFailed);
    }
  };

  const registerImgUrl = useMemo(() => {
    const cmsUrl = siteImages['auth_register']?.url || siteImages['auth_register'] || siteImages['about_hero']?.url || siteImages['about_hero'];
    let img = cmsUrl ? cmsUrl : "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80";
    if (img && typeof img === 'string' && img.includes('/upload/')) {
      img = img.replace('/upload/', '/upload/w_1200,q_auto,f_auto/');
    }
    return img;
  }, [siteImages]);

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Image Side */}
      <div className="hidden lg:block lg:w-1/2 relative bg-[#1A1A1A]">
        <img
          src={registerImgUrl}
          alt="Interior"
          decoding="async"
          onLoad={() => setIsImgReady(true)}
          className={`w-full h-full object-cover transition-opacity duration-300 ${isImgReady ? 'opacity-100' : 'opacity-0'}`}
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Form Side */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 pb-32 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Link to={`/${language}`} className="inline-block mb-12">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-wide text-[#0A0A0A]">
              FEDYK <span className="font-light tracking-normal">Furniture</span>
            </span>
          </Link>

          <h1 className="text-h1 mb-2">{t.auth.register}</h1>
          <p className="text-muted-foreground mb-8">
            {t.auth.registerSubtitle}
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">{t.auth.firstName}</Label>
                <Input
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="h-12 mt-2"
                  required
                  data-testid="register-firstname"
                />
              </div>
              <div>
                <Label htmlFor="last_name">{t.auth.lastName}</Label>
                <Input
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="h-12 mt-2"
                  required
                  data-testid="register-lastname"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">{t.auth.email}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className="h-12 mt-2"
                required
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="h-12 mt-2"
                data-testid="register-phone"
              />
            </div>

            <div>
              <Label htmlFor="password">{t.auth.password}</Label>
              <div className="relative mt-2 mb-2">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  className="h-12 pr-12"
                  required
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {pwd.length > 0 && (
                <div className="p-4 bg-muted/50 border border-border text-sm space-y-2 rounded-md">
                  <p className="font-medium text-foreground">{hints.reqs}</p>
                  <ul className="space-y-1 text-xs">
                    <li className={reqLen ? "text-green-600" : "text-muted-foreground"}>{reqLen ? '✓' : '•'} {hints.len}</li>
                    <li className={reqUpper ? "text-green-600" : "text-muted-foreground"}>{reqUpper ? '✓' : '•'} {hints.up}</li>
                    <li className={reqLower ? "text-green-600" : "text-muted-foreground"}>{reqLower ? '✓' : '•'} {hints.low}</li>
                    <li className={reqDigit ? "text-green-600" : "text-muted-foreground"}>{reqDigit ? '✓' : '•'} {hints.num}</li>
                    <li className={reqSpecial ? "text-green-600" : "text-muted-foreground"}>{reqSpecial ? '✓' : '•'} {hints.spec}</li>
                  </ul>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 p-4 border border-border">
              <input
                type="checkbox"
                id="is_b2b"
                name="is_b2b"
                checked={formData.is_b2b}
                onChange={handleChange}
                className="w-5 h-5 accent-black"
                data-testid="register-b2b-toggle"
              />
              <label htmlFor="is_b2b" className="text-sm cursor-pointer">
                {t.auth.iAmProfessional}
              </label>
            </div>

            {formData.is_b2b && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="space-y-4"
              >
                <div>
                  <Label htmlFor="company_name">{t.auth.companyName}</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    className="h-12 mt-2"
                    required={formData.is_b2b}
                    data-testid="register-company"
                  />
                </div>
                <div>
                  <Label htmlFor="vat_number">{t.auth.vatNumber}</Label>
                  <Input
                    id="vat_number"
                    name="vat_number"
                    value={formData.vat_number}
                    onChange={handleChange}
                    className="h-12 mt-2"
                    data-testid="register-vat"
                  />
                </div>
                <div>
                  <Label htmlFor="role">{t.auth.profession}</Label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full h-12 mt-2 px-3 border border-input bg-background text-sm"
                    data-testid="register-role"
                  >
                    <option value="architect">{t.auth.architect}</option>
                    <option value="designer">{t.auth.interiorDesigner}</option>
                    <option value="contractor">{t.auth.contractor}</option>
                    <option value="retailer">{t.auth.retailer}</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="portfolio_url">{t.auth.portfolioLink || 'Portfolio / Instagram link'}</Label>
                  <Input
                    id="portfolio_url"
                    name="portfolio_url"
                    type="url"
                    value={formData.portfolio_url}
                    onChange={handleChange}
                    className="h-12 mt-2"
                    placeholder="https://instagram.com/your-studio"
                    data-testid="register-portfolio"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1.5 font-light">
                    {t.auth.portfolioHint || 'Helps us verify your studio faster.'}
                  </p>
                </div>

                <div className="pt-2 border-t border-dashed border-border space-y-4">
                  <p className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground">{t.auth.professionalAddress}</p>

                  <div>
                    <Label htmlFor="country">{t.auth.country}</Label>
                    <Input
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="h-12 mt-2"
                      required={formData.is_b2b}
                      placeholder="e.g. Belgium"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">{t.auth.city}</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="h-12 mt-2"
                        required={formData.is_b2b}
                        placeholder="e.g. Brussels"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal_code">{t.auth.postalCode}</Label>
                      <Input
                        id="postal_code"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleChange}
                        className="h-12 mt-2"
                        required={formData.is_b2b}
                        placeholder="e.g. 1000"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="street_address">{t.auth.streetAddress}</Label>
                    <Input
                      id="street_address"
                      name="street_address"
                      value={formData.street_address}
                      onChange={handleChange}
                      className="h-12 mt-2"
                      required={formData.is_b2b}
                      placeholder="Street, building, suite"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={isLoading || (pwd.length > 0 && !isPasswordValid)}
              data-testid="register-submit"
            >
              {isLoading ? t.auth.registering : t.auth.register}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground">
              {t.auth.hasAccount}{' '}
              <Link
                to={`/${language}/login`}
                className="text-foreground hover:underline"
                data-testid="login-link"
              >
                {t.auth.login}
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
